'use client';
import type { NextPage } from 'next';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useState, useRef } from 'react';
import styles from './booking-form.module.scss';
import ButtonSimple from '../ButtonSimple/ButtonSimple';
import VoiceInput from '../VoiceInput/VoiceInput';
import { BookingFormType } from '@/types/heroBlock.types';
import SuccessModal from '../../../SuccessModal/SuccessModal';
import ErrorModal from '../../../ErrorModal/ErrorModal';
import { getAttributionPayload } from '@/utils/getAttributionPayload';

interface IFormInputs {
  name: string;
  email: string;
}

const BlogBookingForm: NextPage<BookingFormType> = ({
  className = '',
  device = 'Desktop',
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<IFormInputs>({
    defaultValues: {
      name: '',
      email: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    try {
      const attribution = getAttributionPayload(formRef.current);
      const response = await fetch('/api/blog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          ...attribution,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      reset();
      setIsSuccess(true);
    } catch (error) {
      setIsError(true);
      console.error('Error submitting form:', error);
    }
  };

  const filterCyrillicFromEmail = (value: string) => {
    return value.replace(/[а-яё]/gi, '');
  };

  return (
    <>
      <section
        className={[styles.bookingform, className].join(' ')}
        data-device={device}
      >
        <div className={styles.firstline}>
          <h3 className={styles.bookYourFlight}>
            Subscribe to be the first to know about news
          </h3>
        </div>
        <form
          ref={formRef}
          className={styles.formblock}
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <div className={styles.inputContainer}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Name is required',
              }}
              render={({ field, fieldState }) => (
                <div>
                  <VoiceInput
                    label="Your name"
                    placeholder="Enter your full name"
                    value={field.value}
                    onChange={field.onChange}
                  />
                  {fieldState.error && (
                    <div className={styles.errorMessage}>
                      {fieldState.error.message}
                    </div>
                  )}
                </div>
              )}
            />

            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Invalid email format',
                },
              }}
              render={({ field, fieldState }) => (
                <div>
                  <VoiceInput
                    label="Email"
                    placeholder="Enter your email address"
                    value={field.value}
                    onChange={(value) =>
                      field.onChange(filterCyrillicFromEmail(value))
                    }
                  />
                  {fieldState.error && (
                    <div className={styles.errorMessage}>
                      {fieldState.error.message}
                    </div>
                  )}
                </div>
              )}
            />
          </div>

          <div className={styles.buttonContainer}>
            <ButtonSimple
              color="accent"
              highContrast
              loading={false}
              size={3}
              state="default ✦"
              variant="solid ✦"
              text={isSubmitting ? 'Subscribe' : 'Subscribe'}
              iconStart={false}
              iconEnd={false}
              focusRing={false}
              designBlendingMode="/img/heroBlock/microphone.svg"
              designBlendingMode1="/img/heroBlock/microphone.svg"
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </section>

      {isSuccess && <SuccessModal onClose={() => setIsSuccess(false)} />}
      {isError && <ErrorModal onClose={() => setIsError(false)} />}
    </>
  );
};

export default BlogBookingForm;
